import { useEffect, useState, useCallback } from "react"
import { supabase } from "@/lib/supabase"
import { useToast } from "@/context/ToastContext"
import { Badge } from "@/components/common/Badge"
import { Button } from "@/components/common/Button"
import { EmptyState } from "@/components/common/EmptyState"
import { LoadingRow } from "@/components/common/Spinner"
import { RefreshCw, Download } from "lucide-react"
import type { CompanySlot } from "@/types"

export function CompanySlotsPage() {
  const { toast } = useToast()
  const [slots, setSlots]   = useState<CompanySlot[]>([])
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from("company_slots")
        .select("*")
        .order("taken_over_at", { ascending: false })
      if (error) throw error
      setSlots((data ?? []) as CompanySlot[])
    } catch { toast("Failed to load slots", "error") }
    finally { setLoading(false) }
  }, [toast])

  useEffect(() => { load() }, [load])

  const totalEarnings  = slots.reduce((s, sl) => s + (sl.monthly_earnings ?? 0), 0)
  const totalDownline  = slots.reduce((s, sl) => s + (sl.downline_size ?? 0), 0)

  return (
    <div className="space-y-5">
      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Slot Earnings", value: `$${totalEarnings.toFixed(2)}`, color: "bg-green-500"  },
          { label: "Total Slots",         value: slots.length,                   color: "bg-zimrah-500" },
          { label: "Combined Downline",   value: totalDownline,                  color: "bg-blue-500"   },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className={`h-1 ${s.color}`} />
            <div className="p-5">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-1">{s.label}</p>
              <p className="text-3xl font-black text-gray-900">{loading ? "—" : s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="flex justify-between">
        <div />
        <div className="flex gap-2">
          <Button variant="secondary" icon={<RefreshCw size={14} />} onClick={load}>Refresh</Button>
          <Button variant="secondary" icon={<Download size={14} />} onClick={() => toast("Export coming in Phase 5", "info")}>Export</Button>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50">
                {["Original Owner", "Taken Over", "Downline", "Monthly Earnings", "Reason", "Actions"].map(h => (
                  <th key={h} className="px-6 py-3 text-left text-[11px] font-bold uppercase tracking-wider text-gray-400">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? <LoadingRow cols={6} /> :
               slots.length === 0 ? (
                <tr><td colSpan={6}>
                  <EmptyState icon="🏢" title="No company slots" subtitle="Slots will appear here after takeovers." />
                </td></tr>
               ) : slots.map(s => (
                <tr key={s.id} className="border-t border-gray-50 hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <p className="font-bold text-sm text-gray-900">{s.original_owner_name ?? "—"}</p>
                    <p className="text-xs text-gray-400">{s.original_owner_email}</p>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(s.taken_over_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="info">{s.downline_size ?? 0}</Badge>
                  </td>
                  <td className="px-6 py-4 font-black text-green-600 tabular-nums">
                    ${(s.monthly_earnings ?? 0).toFixed(2)}
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant="warning">{s.takeover_reason?.replace(/_/g, " ")}</Badge>
                  </td>
                  <td className="px-6 py-4">
                    <Button size="sm" variant="secondary" onClick={() => toast("Slot detail — Phase 5", "info")}>
                      View
                    </Button>
                  </td>
                </tr>
               ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}